import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit } from '@angular/core'
import { ActivatedRoute, Params } from '@angular/router'
import { UntypedFormBuilder, Validators } from '@angular/forms'
import { BehaviorSubject, combineLatest, Observable, of } from 'rxjs'
import { MatchValidator } from '../shared/validators/match.validator'
import { validateEmail } from '../shared/validators/email.validator'
import { LoggingService } from 'src/app/singleton-services/logging/logging.service'
import { UserService } from '../shared/services/user.service'
import { HttpErrorResponse } from '@angular/common/http'
import { DuplicateAsyncValidators } from '../shared/validators/duplicate-async.validators'
import { startWith, filter, flatMap, map, catchError, take, share, mergeMap, shareReplay } from 'rxjs/operators'

@Component({
  selector: 'flo-reset-password',
  templateUrl: './reset-password.component.html',
  styleUrls: ['./reset-password.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ResetPasswordComponent implements OnInit {
  private FORGOT_PASSWORD_FORM = 'FORGOT_PASSWORD_FORM'
  private FORGOT_PASSWORD_SUCCESS = 'FORGOT_PASSWORD_SUCCESS'
  private RESET_PASSWORD_SUCCESS = 'RESET_PASSWORD_SUCCESS'

  private viewSource = new BehaviorSubject(this.FORGOT_PASSWORD_FORM)
  private view$ = this.viewSource.asObservable()
  private token$ = this.ar.params.pipe(map((params: Params) => params.token))
  private isTokenValid$ = this.token$.pipe(
    filter(token => !!token),
    flatMap(token => this.userService.checkToken(token)),
    map(() => true),
    catchError(() => of(false))
  )

  public config$ = combineLatest([this.view$, this.token$, this.isTokenValid$.pipe(startWith(false))])
    .pipe(
      map(([view, token, isTokenValid]: [view: string, token: string, isTokenValid: boolean]) => {
        return {
          showForgotPasswordForm: !token && view === this.FORGOT_PASSWORD_FORM,
          showForgotPasswordSuccess: !token && view === this.FORGOT_PASSWORD_SUCCESS,
          showResetPasswordForm: !!token && isTokenValid && view !== this.RESET_PASSWORD_SUCCESS,
          showTokenExpired: !!token && !isTokenValid,
          showResetPasswordSuccess: !!token && view === this.RESET_PASSWORD_SUCCESS
        }
      })
    )
    .pipe(share())

  public userIsFromUSAW$ = this.ar.queryParams.pipe(map((params: Params) => params.usaw === 'true'))
  public isUSAW = false
  public showResetUSAWPassword = false
  public forgotPasswordSubmitting = false
  public resetPasswordSubmitting = false
  public resetUSAWPasswordSubmitting = false
  public showEmailSpinner$: Observable<boolean>
  public forgotPasswordForm = this.fb.group({
    email: ['', [Validators.required, validateEmail], [this.dav.validateEmailExists()]]
  })
  public resetPasswordForm = this.fb.group(
    {
      password: ['', [Validators.required, Validators.minLength(6), Validators.maxLength(125)]],
      confirm_password: ['', [Validators.required, Validators.minLength(6), Validators.maxLength(125)]]
    },
    {
      validator: MatchValidator.areEqual('password', 'confirm_password', 'password_mismatch')
    }
  )

  public resetUSAWPasswordForm = this.fb.group({
    USAW_password: ['', [Validators.required, Validators.minLength(6), Validators.maxLength(125)]]
  })

  public get canSubmitForgotPassword() {
    return this.forgotPasswordForm.valid && !this.forgotPasswordForm.pending && !this.forgotPasswordSubmitting
  }

  public get canSubmitResetPassword() {
    return this.resetPasswordForm.valid && !this.resetPasswordForm.pending && !this.resetPasswordSubmitting
  }

  public get canSubmitResetUSAWPassword() {
    return this.resetUSAWPasswordForm.valid && !this.resetUSAWPasswordForm.pending && !this.resetUSAWPasswordSubmitting
  }

  public get email() {
    return this.forgotPasswordForm.get('email')
  }

  public get password() {
    return this.resetPasswordForm.get('password')
  }

  public get USAWPassword() {
    return this.resetUSAWPasswordForm.get('USAW_password')
  }

  public get confirmPassword() {
    return this.resetPasswordForm.get('confirm_password')
  }

  public showFormErrorComponent() {
    return (
      (this.forgotPasswordForm.controls.email.touched &&
        this.forgotPasswordForm.controls.email.invalid &&
        !this.forgotPasswordForm.controls.email.hasError('unclaimed_account')) ||
      this.forgotPasswordForm.controls.email.hasError('email_does_not_exist')
    )
  }

  public toggleShowUSAWPassword(event: Event) {
    event.stopPropagation()
    this.showResetUSAWPassword = !this.showResetUSAWPassword
  }

  private get emailValue() {
    return this.email && this.email.value
  }

  private get passwordValue() {
    return this.password && this.password.value
  }

  private get USAWPasswordValue() {
    return this.USAWPassword && this.USAWPassword.value
  }

  constructor(
    private ar: ActivatedRoute,
    private userService: UserService,
    private logger: LoggingService,
    private changeRef: ChangeDetectorRef,
    private dav: DuplicateAsyncValidators,
    private fb: UntypedFormBuilder
  ) {}

  ngOnInit() {
    if (this.email) {
      this.showEmailSpinner$ = this.email.statusChanges.pipe(map(status => status === 'PENDING'))
    }

    this.userIsFromUSAW$.pipe(take(1)).subscribe(value => (this.isUSAW = value))
  }

  public sendEmail() {
    if (!this.canSubmitForgotPassword) {
      return
    }
    this.forgotPasswordSubmitting = true

    this.userService
      .sendForgotPasswordEmail(this.emailValue)
      .pipe(take(1))
      .subscribe(
        () => {
          this.forgotPasswordSubmitting = false
          this.viewSource.next(this.FORGOT_PASSWORD_SUCCESS)
          this.changeRef.markForCheck()
        },
        (err: HttpErrorResponse) => {
          this.forgotPasswordSubmitting = false
          this.logger.error(err.message, err.error)
          const errorType = err.status === 400 ? { email_format: true } : { server_error: true }
          if (!!this.email) {
            this.email.setErrors(errorType)
          }
          this.changeRef.markForCheck()
        }
      )
  }

  public resetPassword() {
    if ((!this.isUSAW && !this.canSubmitResetPassword) || (this.isUSAW && !this.canSubmitResetUSAWPassword)) {
      return
    }

    this.resetPasswordSubmitting = true
    this.resetUSAWPasswordSubmitting = true

    this.token$
      .pipe(
        mergeMap(token =>
          this.userService.resetPassword(token, !this.isUSAW ? this.passwordValue : this.USAWPasswordValue)
        ),
        take(1)
      )
      .subscribe({
        next: () => {
          this.resetPasswordSubmitting = false
          this.resetUSAWPasswordSubmitting = false

          this.viewSource.next(this.RESET_PASSWORD_SUCCESS)
          this.changeRef.markForCheck()
        },
        error: (err: HttpErrorResponse) => {
          this.resetPasswordSubmitting = false
          this.resetUSAWPasswordSubmitting = false

          this.logger.error(err.message, err.error)

          if (!this.isUSAW) {
            if (this.confirmPassword && err.error.message) {
              this.resetPasswordForm.setErrors({ serverError: err.error.message })
            } else if (this.confirmPassword) {
              // Fallback to generic server error
              this.confirmPassword.setErrors({ server_error: true })
            }
          } else {
            if (this.USAWPassword && err.error.message) {
              this.resetUSAWPasswordForm.setErrors({ serverError: err.error.message })
            } else if (this.USAWPassword) {
              // Fallback to generic server error
              this.USAWPassword.setErrors({ server_error: true })
            }
          }

          this.changeRef.markForCheck()
        }
      })
  }
}
